import { 
  BookOpen, 
  Upload, 
  Activity, 
  ChevronRight, 
  CheckCircle2, 
  Database, 
  FileText, 
  Cpu, 
  Layers,
  Sparkles
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { cn } from '../../../lib/utils.js';
import { 
  TOTAL_MASTER_DOCS, 
  TOTAL_PAGES, 
  TOTAL_CHUNKS, 
  TOTAL_SKU_LINKS 
} from '../executive-data.js';

export function RagPillarCard({
  docCount = null,
  pageCount = null,
  chunkCount = null,
  skuLinkCount = null,
  vectorizeStatus = null,
  fts5Status = null,
  isLive = false,
  onOpenUpload,
  onOpenTab,
  isDetailed = false,
  className,
}) {
  const handleOpenUpload = () => {
    if (onOpenUpload) {
      onOpenUpload();
    } else if (onOpenTab) {
      onOpenTab('rag');
    }
  };

  const handleOpenChangelog = () => {
    if (onOpenTab) {
      onOpenTab('changelog');
    }
  };

  const displayDocCount = docCount !== null && docCount !== undefined ? docCount : TOTAL_MASTER_DOCS;
  const displayPageCount = pageCount !== null && pageCount !== undefined ? pageCount : TOTAL_PAGES;
  const displayChunkCount = chunkCount !== null && chunkCount !== undefined ? chunkCount : TOTAL_CHUNKS;
  const displaySkuLinkCount = skuLinkCount !== null && skuLinkCount !== undefined ? skuLinkCount : TOTAL_SKU_LINKS;

  return (
    <div
      data-testid="pillar-card-rag"
      className={cn(
        'flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 shadow-xs transition-all hover:border-[var(--primary,#e3000b)] hover:shadow-sm',
        className
      )}
    >
      <div className="space-y-4">
        {/* Header Badge Strip */}
        <div className="flex items-center justify-between">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-[var(--primary,#e3000b)] border border-red-200">
            <BookOpen className="h-5 w-5" />
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-red-50 text-[var(--primary,#e3000b)] border-red-200 text-xs font-bold">
              Säule 3
            </Badge>
            {isLive ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-[11px] font-semibold">
                Vectorize BGE-M3
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-[11px] font-medium">
                Vectorize (Demo)
              </Badge>
            )}
          </div>
        </div>

        {/* Title & Scope Description */}
        <div>
          <h3 className="font-display text-base font-bold text-slate-900 leading-snug">
            RAG Wissenskorpus &amp; Upload
          </h3>
          <p className="mt-1 text-xs text-slate-600 leading-relaxed">
            {isLive ? (
              <>Zentraler Vektor- und Volltextindex über <strong className="font-mono tabular-nums font-semibold text-slate-900">{displayDocCount.toLocaleString('de-DE')} Master-Dokumente</strong> mit Hybrid-Retrieval (BGE-M3 + FTS5 BM25).</>
            ) : (
              <>Exemplarischer Vektor- und Volltextindex-Entwurf (<strong className="font-mono tabular-nums font-semibold text-slate-900">{displayDocCount.toLocaleString('de-DE')} Docs (Demo)</strong>) für Hybrid-Retrieval-Vorschau.</>
            )}
          </p>
        </div>

        {/* Telemetry Metrics Grid */}
        <div className="space-y-2.5 rounded-xl bg-slate-50/80 p-3.5 text-xs border border-slate-100">
          <div className="flex items-center justify-between" data-testid="rag-doc-count">
            <span className="flex items-center gap-1.5 text-slate-600">
              <FileText className="h-3.5 w-3.5 text-slate-400" />
              <span>Master-Dokumente:</span>
            </span>
            <span className="font-mono tabular-nums font-bold text-slate-900">
              {isLive ? `${displayDocCount} Docs (${displayPageCount.toLocaleString('de-DE')} S.)` : `${displayDocCount} Docs (Demo)`}
            </span>
          </div>

          <div className="flex items-center justify-between" data-testid="rag-page-count">
            <span className="flex items-center gap-1.5 text-slate-600">
              <BookOpen className="h-3.5 w-3.5 text-slate-400" />
              <span>Indizierte Seiten:</span>
            </span>
            <span className="font-mono tabular-nums font-bold text-slate-900">
              {isLive ? `${displayPageCount.toLocaleString('de-DE')} Seiten (OCR verarbeitet)` : `${displayPageCount.toLocaleString('de-DE')} Seiten (Muster)`}
            </span>
          </div>

          <div className="flex items-center justify-between" data-testid="rag-chunk-count">
            <span className="flex items-center gap-1.5 text-slate-600">
              <Cpu className="h-3.5 w-3.5 text-slate-400" />
              <span>Vektor-Chunks (1024d):</span>
            </span>
            <span className="font-mono tabular-nums font-bold text-slate-900">
              {isLive ? `${displayChunkCount.toLocaleString('de-DE')} Chunks` : `${displayChunkCount.toLocaleString('de-DE')} Chunks (Muster)`}
            </span>
          </div>

          <div className="flex items-center justify-between" data-testid="rag-sku-links">
            <span className="flex items-center gap-1.5 text-slate-600">
              <Layers className="h-3.5 w-3.5 text-slate-400" />
              <span>SKU-Verknüpfungen:</span>
            </span>
            <span className="font-mono tabular-nums font-bold text-slate-900">
              {isLive ? `${displaySkuLinkCount.toLocaleString('de-DE')} (D1 SKU-Links)` : `${displaySkuLinkCount.toLocaleString('de-DE')} Links (Demo)`}
            </span>
          </div>

          <div className="flex items-center justify-between pt-1 border-t border-slate-200">
            <span className="flex items-center gap-1.5 text-slate-600">
              <Database className="h-3.5 w-3.5 text-slate-400" />
              <span>Index-Status:</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 font-semibold text-slate-800" data-testid="rag-vectorize-status">
                {isLive && vectorizeStatus === 'HEALTHY' ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                    <span className="text-emerald-700">Vectorize Aktiv</span>
                  </>
                ) : (
                  <span>{isLive && vectorizeStatus ? `Vectorize ${vectorizeStatus}` : 'Vectorize Demo'}</span>
                )}
              </span>
              <span className="text-slate-300">•</span>
              <span className="inline-flex items-center gap-1 font-semibold text-slate-800" data-testid="rag-fts5-status">
                {isLive && fts5Status === 'HEALTHY' ? (
                  <span className="text-emerald-700">FTS5 Healthy</span>
                ) : (
                  <span>{isLive && fts5Status ? `FTS5 ${fts5Status}` : 'FTS5 Demo'}</span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Footer */}
      <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="rag-action-upload"
          onClick={handleOpenUpload}
          className="text-xs font-bold gap-1 text-[var(--primary,#e3000b)] hover:bg-[var(--primary,#e3000b)]/5 cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
        >
          <Upload className="h-3.5 w-3.5" />
          <span>Upload-Center</span>
          <ChevronRight className="h-3 w-3 ml-0.5" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="rag-action-changelog"
          onClick={handleOpenChangelog}
          className="text-xs text-slate-600 hover:text-slate-900 gap-1 cursor-pointer"
        >
          <Activity className="h-3 w-3" />
          <span>Update-Feed</span>
        </Button>
      </div>
    </div>
  );
}
